import { logger } from './logger';

// Resource preloading utilities
export interface PreloadOptions {
  as: 'script' | 'style' | 'image' | 'font' | 'fetch';
  type?: string;
  crossorigin?: 'anonymous' | 'use-credentials';
  media?: string;
}

export class ResourcePreloader {
  private static preloadedResources = new Set<string>();

  // Preload a single resource
  static preload(href: string, options: PreloadOptions): Promise<void> {
    return new Promise((resolve, reject) => {
      // Skip if already preloaded
      if (this.preloadedResources.has(href)) {
        resolve();
        return;
      }

      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = href;
      link.as = options.as;

      if (options.type) link.type = options.type;
      if (options.crossorigin) link.crossOrigin = options.crossorigin;
      if (options.media) link.media = options.media;

      link.onload = () => {
        this.preloadedResources.add(href);
        resolve();
      };

      link.onerror = () => {
        logger.warn(`Failed to preload resource: ${href}`);
        reject(new Error(`Failed to preload: ${href}`));
      };

      document.head.appendChild(link);
    });
  }

  // Preload multiple resources
  static preloadMultiple(
    resources: Array<{ href: string; options: PreloadOptions }>,
  ): Promise<void[]> {
    return Promise.all(resources.map(({ href, options }) => this.preload(href, options)));
  }

  // Preload fonts with font-display: swap
  static preloadFonts(
    fonts: Array<{ family: string; src: string; weight?: string }>,
  ): Promise<void[]> {
    return Promise.all(
      fonts.map((font) => {
        return new Promise<void>((resolve, reject) => {
          const fontFace = new FontFace(font.family, `url(${font.src})`, {
            weight: font.weight || 'normal',
            display: 'swap',
          });

          fontFace
            .load()
            .then(() => {
              (document.fonts as FontFaceSet & { add: (fontFace: FontFace) => void }).add(fontFace);
              resolve();
            })
            .catch(reject);
        });
      }),
    );
  }

  // Preload critical route components
  static preloadRouteComponents(routes: string[]): void {
    routes.forEach((route) => {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = route;
      document.head.appendChild(link);
    });
  }

  // Check if resource is already preloaded
  static isPreloaded(href: string): boolean {
    return this.preloadedResources.has(href);
  }

  // Clear preloaded resources cache
  static clearCache(): void {
    this.preloadedResources.clear();
  }
}

// Hook for preloading resources in React components
export const useResourcePreloader = () => {
  const preloadResource = (href: string, options: PreloadOptions) => {
    return ResourcePreloader.preload(href, options);
  };

  const preloadMultiple = (resources: Array<{ href: string; options: PreloadOptions }>) => {
    return ResourcePreloader.preloadMultiple(resources);
  };

  const preloadFonts = (fonts: Array<{ family: string; src: string; weight?: string }>) => {
    return ResourcePreloader.preloadFonts(fonts);
  };

  return {
    preloadResource,
    preloadMultiple,
    preloadFonts,
    isPreloaded: ResourcePreloader.isPreloaded,
  };
};

// Critical resource preloader for app initialization
export const preloadCriticalResources = async () => {
  const criticalResources = [
    {
      href: '/logo.svg',
      options: { as: 'image' as const, type: 'image/svg+xml' },
    },
  ];

  try {
    await ResourcePreloader.preloadMultiple(criticalResources);
    logger.log('Critical resources preloaded successfully');
  } catch (error) {
    logger.warn('Some critical resources failed to preload:', error);
  }
};

// Enhanced banner preloader for LCP optimization
export const preloadFirstBanner = async (bannerUrl: string) => {
  try {
    // Create preload link with high priority for LCP
    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.as = 'image';
    preloadLink.href = bannerUrl;
    preloadLink.setAttribute('fetchpriority', 'high');
    preloadLink.crossOrigin = 'anonymous';

    // Add to document head
    document.head.appendChild(preloadLink);

    // Also preload using ResourcePreloader for better caching
    await ResourcePreloader.preload(bannerUrl, {
      as: 'image' as const,
      crossorigin: 'anonymous' as const,
    });

    logger.log('✅ First banner preloaded for LCP optimization:', bannerUrl);
  } catch (error) {
    logger.warn('⚠️ Failed to preload first banner:', error);
  }
};

// Preload critical fonts for better LCP
export const preloadCriticalFonts = () => {
  const criticalFonts = [
    // Inter font - Regular weight (400)
    {
      href: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiJ-Ek-_EeA.woff2',
      type: 'font/woff2',
      family: 'Inter',
      weight: '400',
    },
    // Inter font - Medium weight (500)
    {
      href: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuGKYAZ9hiA.woff2',
      type: 'font/woff2',
      family: 'Inter',
      weight: '500',
    },
    // Inter font - SemiBold weight (600)
    {
      href: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuFuYAZ9hiA.woff2',
      type: 'font/woff2',
      family: 'Inter',
      weight: '600',
    },
    // Roboto font - Regular weight (400)
    {
      href: 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxKKTU1Kg.woff2',
      type: 'font/woff2',
      family: 'Roboto',
      weight: '400',
    },
  ];

  criticalFonts.forEach((font) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'font';
    link.type = font.type;
    link.href = font.href;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);

    logger.log(`🔤 Preloaded font: ${font.family} (${font.weight})`);
  });

  logger.log('✅ Critical fonts preloaded successfully');
};
